/**
 * Email Verification Component
 * Handles email verification flow with resend functionality
 */
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    LinearProgress,
    Chip,
    Divider,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Email as EmailIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    Refresh as RefreshIcon,
    Schedule as ScheduleIcon,
    Security as SecurityIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../api/axiosInstance';
import { updateTokens } from '../features/auth/authSlice';

const StyledCard = styled(Card)(({ theme }) => ({
    maxWidth: 500,
    margin: '0 auto',
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
    background: theme.palette.background.paper,
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 600,
    padding: '12px 24px',
}));

const EmailVerification = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.auth);

    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [resending, setResending] = useState(false);
    const [verificationStatus, setVerificationStatus] = useState(null);
    const [error, setError] = useState(null);
    const [countdown, setCountdown] = useState(0);
    const [canResend, setCanResend] = useState(true);

    // Auto-verify if token is provided
    useEffect(() => {
        if (token) {
            handleVerification(token);
        }
    }, [token]);

    // Countdown timer for resend button
    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            setCanResend(true);
        }
    }, [countdown]);

    // Check if user is already verified
    useEffect(() => {
        if (user && user.email_verified) {
            setVerificationStatus('verified');
        }
    }, [user]);

    const handleVerification = async(verificationToken) => {
        setVerifying(true);
        setError(null);

        try {
            const response = await axiosInstance.get(`/api/v1/auth/verify-email/${verificationToken}/`);

            if (response.data.success) {
                setVerificationStatus('verified');

                // Update user state
                dispatch(updateTokens({
                    user: response.data.user,
                    accessToken: localStorage.getItem('accessToken'),
                    refreshToken: localStorage.getItem('refreshToken')
                }));

                toast.success('Email verified successfully!');

                // Redirect to dashboard after a short delay
                setTimeout(() => {
                    navigate(`/${user?.role || 'patient'}-dashboard`);
                }, 2000);

            } else {
                setError(response.data.error);
                setVerificationStatus('failed');
            }
        } catch (error) {
            console.error('Email verification failed:', error);
            setError(error.response?.data?.error || 'Email verification failed');
            setVerificationStatus('failed');
        } finally {
            setVerifying(false);
        }
    };

    const handleResendVerification = async() => {
        setResending(true);
        setError(null);

        try {
            const response = await axiosInstance.post('/api/v1/auth/resend-verification-email/', {
                email: user?.email
            });

            if (response.data.success) {
                toast.success('Verification email sent successfully!');
                setCanResend(false);
                setCountdown(60); // 60 seconds cooldown
            } else {
                setError(response.data.error);
            }
        } catch (error) {
            console.error('Resend verification failed:', error);
            setError(error.response?.data?.error || 'Failed to resend verification email');
        } finally {
            setResending(false);
        }
    };

    const getStatusIcon = () => {
        switch (verificationStatus) {
            case 'verified':
                return <CheckIcon color = "success"
                sx = {
                    { fontSize: 48 }
                }
                />;
            case 'failed':
                return <ErrorIcon color = "error"
                sx = {
                    { fontSize: 48 }
                }
                />;
            default:
                return <EmailIcon color = "primary"
                sx = {
                    { fontSize: 48 }
                }
                />;
        }
    };

    const getStatusColor = () => {
        switch (verificationStatus) {
            case 'verified':
                return 'success';
            case 'failed':
                return 'error';
            default:
                return 'primary';
        }
    };

    const getStatusMessage = () => {
        switch (verificationStatus) {
            case 'verified':
                return 'Email verified successfully!';
            case 'failed':
                return 'Email verification failed';
            default:
                return 'Please verify your email address';
        }
    };

    const getStatusDescription = () => {
        switch (verificationStatus) {
            case 'verified':
                return 'Your email has been verified and your account is now fully activated.';
            case 'failed':
                return 'There was an error verifying your email. Please try again or request a new verification email.';
            default:
                return 'We sent a verification email to your email address. Please check your inbox and click the verification link.';
        }
    };

    if (verifying) {
        return ( <
            Box display = "flex"
            flexDirection = "column"
            alignItems = "center"
            p = { 4 } >
            <
            CircularProgress size = { 48 }
            /> <
            Typography variant = "h6"
            sx = {
                { mt: 2 }
            } >
            Verifying your email... <
            /Typography> <
            LinearProgress sx = {
                { width: '100%', mt: 2 }
            }
            /> < /
            Box >
        );
    }

    return ( <
        Box p = { 3 }
        display = "flex"
        justifyContent = "center"
        minHeight = "100vh"
        alignItems = "center" >
        <
        StyledCard >
        <
        CardContent sx = {
            { p: 4 }
        } >
        <
        Box textAlign = "center"
        mb = { 3 } > { getStatusIcon() } <
        Typography variant = "h4"
        sx = {
            { mt: 2, mb: 1 }
        } > { getStatusMessage() } <
        /Typography> <
        Typography variant = "body1"
        color = "text.secondary" > { getStatusDescription() } <
        /Typography> < /
        Box >

        {
            error && ( <
                Alert severity = "error"
                sx = {
                    { mb: 3 }
                } > { error } <
                /Alert>
            )
        }

        {
            verificationStatus === 'verified' && ( <
                Alert severity = "success"
                sx = {
                    { mb: 3 }
                } >
                <
                Typography variant = "body2" >
                Redirecting you to your dashboard... <
                /Typography> < /
                Alert >
            )
        }

        {
            verificationStatus !== 'verified' && ( <
                Box >
                <
                Divider sx = {
                    { my: 3 }
                }
                />

                <
                Typography variant = "h6"
                gutterBottom >
                Didn 't receive the email? < /
                Typography >

                <
                Typography variant = "body2"
                color = "text.secondary"
                paragraph >
                Check your spam folder or request a new verification email. <
                /Typography>

                <
                Box display = "flex"
                gap = { 2 }
                mt = { 3 } >
                <
                StyledButton variant = "contained"
                onClick = { handleResendVerification }
                disabled = { resending || !canResend }
                startIcon = { resending ? < CircularProgress size = { 20 } /> : <RefreshIcon / > }
                fullWidth > { resending ? 'Sending...' : 'Resend Verification Email' } <
                /StyledButton> < /
                Box >

                {!canResend && countdown > 0 && ( <
                        Box textAlign = "center"
                        mt = { 2 } >
                        <
                        Chip icon = { < ScheduleIcon / > }
                        label = { `Resend available in ${countdown}s` }
                        color = "warning"
                        variant = "outlined" /
                        >
                        <
                        /Box>
                    )
                } <
                /Box>
            )
        }

        <
        Divider sx = {
            { my: 3 }
        }
        />

        <
        Box >
        <
        Typography variant = "h6"
        gutterBottom >
        Need help ?
        <
        /Typography> <
        Typography variant = "body2"
        color = "text.secondary" >
        If you 're having trouble verifying your email, please contact our support team. < /
        Typography > <
        Box display = "flex"
        gap = { 1 }
        mt = { 2 } >
        <
        Button variant = "outlined"
        size = "small" >
        Contact Support <
        /Button> <
        Button variant = "outlined"
        size = "small" >
        Help Center <
        /Button> < /
        Box > <
        /Box> < /
        CardContent > <
        /StyledCard> < /
        Box >
    );
};

export default EmailVerification;